import {LogoutIcon, MenuIcon, XIcon} from '@heroicons/react/outline';
import type {JSX} from 'react';
import {useState} from 'react';
import {Outlet} from 'react-router-dom';

import SystemHealthLink from '@/components/SystemHealthLink';
import {useDarkMode} from '@/context/ThemeProviderContext';
import {useAuth} from '@/hooks/useAuth';

import AfriexLogo from './AfriexLogo';
import Button from './Button';
import DarkModeToggle from './DarkModeToggle';
import Nav from './Nav';

export default function Layout(): JSX.Element {
  const {logout, adminUser} = useAuth();
  const {isDark, toggleDarkMode} = useDarkMode();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-background dark:bg-dark-background">
      {/* Mobile Header */}
      <header className="lg:hidden flex justify-between items-end p-4 border-b dark:border-dark-navBorder">
        <div className="items-center">
          <AfriexLogo label="Afriex Admin" />
        </div>
        <button
          className="p-2 text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-md lg:hidden"
          onClick={() => setMenuOpen(true)}
        >
          <MenuIcon className="h-6 w-6" />
        </button>
      </header>

      {/* Sidebar Drawer */}
      <div
        className={`fixed z-40 top-0 left-0 h-full w-64 transform border-r dark:border-[#343538] transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:relative lg:translate-x-0 lg:flex`}
      >
        <Sidebar
          adminUser={adminUser}
          logout={logout}
          toggleDarkMode={toggleDarkMode}
          isDark={isDark}
          closeMenu={() => setMenuOpen(false)}
        />
      </div>

      {/* Backdrop for mobile */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black opacity-50 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-hidden bg-background dark:bg-dark-background relative mt-0 dark:text-white">
        <div
          data-cy="application-page-container"
          className="mx-0 md:mx-2 mb-40 flex-1 overflow-auto overflow-y-auto h-full"
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}

type SidebarProps = {
  adminUser?: {
    firstName: string | null;
    email: string | null;
  };
  logout: () => void;
  toggleDarkMode: () => void;
  isDark: boolean;
  closeMenu: () => void;
};

const Sidebar: React.FC<SidebarProps> = ({
  adminUser,
  logout,
  toggleDarkMode,
  isDark,
  closeMenu,
}) => (
  <aside className="w-64 h-full flex flex-col border-r dark:border-dark-navBorder bg-background dark:bg-dark-background">
    <div className="py-5 px-4 flex justify-between items-center">
      <AfriexLogo label="Afriex Admin" />
      <button className="lg:hidden" onClick={closeMenu}>
        <XIcon className="h-5 w-5 text-gray-500 dark:text-white" />
      </button>
    </div>

    <div className="flex-1 flex flex-col h-0">
      <Nav closeMenu={closeMenu} />
    </div>

    <div className="flex flex-col py-4 px-3 border-t dark:border-dark-navBorder text-slate-500 gap-4">
      <SystemHealthLink />
      <DarkModeToggle isDark={isDark} toggleDarkMode={toggleDarkMode} />

      <div className="flex gap-2 items-center">
        <img
          src={`https://ui-avatars.com/api/?name=${
            adminUser?.firstName || 'Afriex Admin'
          }&background=random`}
          alt="user-avatar"
          className="w-6 h-6 rounded-full"
        />
        <div className="flex flex-col truncate">
          <p className="text-sm font-semibold truncate w-36 dark:text-white">
            {adminUser?.firstName}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate w-36">
            {adminUser?.email}
          </p>
        </div>
      </div>
      <div className="flex w-max hover:text-cyan-700 text-gray-700 group-hover:text-cyan-700 dark:text-gray-100">
        <Button
          variant="unStyled"
          onClick={logout}
          leftIcon={<LogoutIcon className="w-5 h-5" />}
          className="text-sm font-medium text-left"
          data-cy="admin-logout-button"
        >
          Logout
        </Button>
      </div>
    </div>
  </aside>
);
